<!-- SelectNoteUser.vue -->
<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";
import AnchoredPanel from "@/components/Generics/AnchoredPanel.vue";
import { useNoteStore } from "@/stores/useNoteStore";

import api from "@/services/api"; // ✅ IMPORTANTE: Para pegar a baseURL

const props = defineProps({
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(["apply-selection", "close"]);
const noteStore = useNoteStore();

const users = computed(() => noteStore.noteCreators || []);
const searchTerm = ref("");
const searchInputRef = ref(null);
const tempSelectedUserIds = ref([]);

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      tempSelectedUserIds.value = [...noteStore.selectedUserIdsForFilter];
    } else {
      searchTerm.value = "";
    }
  },
  { immediate: true }
);

// ✅ Lógica de normalização idêntica ao resto do sistema
function normalizeAvatarPath(path) {
  if (!path) return "";
  
  // Se for URL externa (Google/Facebook), retorna direto
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  
  // Pega a URL base da API e remove o "/api" do final
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  
  // Garante que o caminho comece com "/"
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  
  return `${base}${cleanPath}`;
}

// ✅ inclui Sys (sem remover ninguém)
const filteredUsers = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  const base = users.value;

  if (!term) return base;

  return base.filter((user) => (user.name || "").toLowerCase().includes(term));
});


const allSelected = computed({
  get: () =>
    users.value.length > 0 &&
    users.value.every(u => tempSelectedUserIds.value.includes(u.id)),
  set: (val) => {
    tempSelectedUserIds.value = val ? users.value.map(u => u.id) : [];
  },
});

function toggleAll() {
  allSelected.value = !allSelected.value;
}

function applySelection() {
  noteStore.selectedUserIdsForFilter = [...tempSelectedUserIds.value];
  emit("apply-selection", true);
}

</script>

<template>
  <AnchoredPanel
    :open="isOpen"
    placement="side"
    align="left"
    :gap="8"
    @close="emit('close')"
    @opened="searchInputRef?.focus()"
  >
    <div class="user-dropdown-container" role="dialog" aria-label="Filtrar por usuário" @click.stop @mousedown.stop>
      <!-- header (tight, só para espaçamento como no NoteFilter) -->
      <div class="search-section">
        <label for="search-dropdown" class="sr-only">Buscar</label>
        <input
          id="search-dropdown"
          ref="searchInputRef"
          v-model="searchTerm"
          type="text"
          class="search-input"
          placeholder="Buscar usuários"
          autocomplete="off"
          @keydown.enter.prevent="applySelection"
        />
      </div>

      <ul class="users-list">
        <li v-if="users.length > 0" class="user-item" @click="toggleAll">
          <div class="user-label">
            <div class="user-left">
              <span class="user-name" style="font-weight: 600;">Todos os usuários</span>
            </div>
            <input type="checkbox" class="checkbox-style-modal" v-model="allSelected" @click.stop />
          </div>
        </li>
        <li v-if="users.length > 0" class="list-divider" role="separator"></li>

        <li v-for="user in filteredUsers" :key="user.id" class="user-item">
          <label :for="`dropdown-user-${user.id}`" class="user-label">
            <div class="user-left">
              <img
                v-if="user.photoPath"
                class="user-avatar"
                :src="normalizeAvatarPath(user.photoPath)"
                :alt="`${user.name} image`"
              />
              <span v-else class="user-icon">
                <i class="fas fa-user"></i>
              </span>
              <span class="user-name" :title="user.name">{{ user.name }}</span>
            </div>

            <input
              :id="`dropdown-user-${user.id}`"
              v-model="tempSelectedUserIds"
              :value="user.id"
              type="checkbox"
              class="checkbox-style-modal"
            />
          </label>
        </li>

        <li v-if="filteredUsers.length === 0" class="no-user-item">
          Nenhum usuário encontrado.
        </li>
      </ul>

      <div class="modal-actions">
        <button type="button" class="btn btn-primary btn-sm" @click="applySelection">
          Filtrar
        </button>
      </div>
    </div>
  </AnchoredPanel>
</template>

<style scoped>
/* =========================
   ✅ Harmonizado com NoteFilter.vue
   - mesma “pegada tight”: fontes menores, paddings enxutos, gaps menores
   - NÃO altera cores (mantidas)
   ========================= */

.user-dropdown-container {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  width: 220px; /* mais tight que 250px */
  display: flex;
  flex-direction: column;

}

/* Busca */
.search-section {
  padding: 10px 10px 8px;
}

/* A .search-input canônica reserva 2.5rem à esquerda para o ícone de lupa;
   aqui não há ícone, então o texto e o cursor começam junto à borda. */
.search-input {
  padding-left: 0.75rem;
}

/* Foco no mesmo desenho do campo de "Por título" (SelectFieldFilter): borda
   lilás com o anel fino azul, no lugar do anel largo da .search-input canônica. */
.search-input:focus {
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-field);
}

/* Lista */
.users-list {
  list-style: none;
  padding: 4px 0;
  margin: 0;

  font-size: 0.82rem;
  color: var(--text-dark);
  font-weight: 500;

  overflow-y: auto;
  max-height: 190px; /* levemente mais tight */
}

.user-item {
  padding: 4px 10px; /* mais tight */
}

.user-item:hover {
  background-color: var(--surface-hover);
  color: var(--text-dark);
}
.user-item:active {
  transform: none;
}

.user-label {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  gap: 8px; /* controla espaçamento geral */
}

.user-left {
  display: inline-flex;
  align-items: center;
  min-width: 0; /* permite ellipsis */
}

.user-avatar {
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}

.user-icon {
  width: 22px;
  height: 22px;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--neutral-200);
  color: var(--text-light);
  font-size: 0.7rem;
  flex: 0 0 auto;
}

.user-name {
  display: inline-block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400; /* ✅ remove negrito do nome */
  line-height: 1.15; /* igual ao NoteFilter */
}

.list-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 4px 10px;
  list-style: none;
}

/* vazio */
.no-user-item {
  padding: 8px 10px;
  text-align: center;
  color: #999;
  font-style: italic;
  font-size: 0.8rem;
}

/* Checkbox (mantido visual / cores) */
.checkbox-style-modal {
  width: 0.95rem; /* casa com NoteFilter (0.95rem) */
  height: 0.95rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  flex-shrink: 0;
  margin: 0;
  transition: all 0.15s ease-in-out;
}

.checkbox-style-modal:hover {
  border-color: var(--brand-light);
}

.checkbox-style-modal:checked {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
}

.checkbox-style-modal:checked:hover {
  background-color: var(--brand-light-hover);
  border-color: var(--brand-light-hover);
}

.checkbox-style-modal:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Ações */
.modal-actions {
  padding: 8px 10px;
  border-top: 1px solid var(--border-color);
  margin: 6px 10px 0 10px; /* tight */
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem; /* tight */
}

.btn-sm {
  height: 26px;
  padding: 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}

</style>